import type { RowErrorReason } from '../components/ui/RowError';
import { isDiscountLine, validateDiscountLine, type DiscountFields, type DiscountableLine } from './ticket-discount';

/**
 * WHAT A TICKET LINE OWES BEFORE IT CAN BE SAVED, decided once for both editors.
 *
 * The office editor (NewTicketScreen) and the field editor
 * (screens/field/FieldTicketsScreen) each grew their own copy of the same
 * description/type check, and the two had already started to drift — one a
 * named function, one written out at the save call site. Rate and quantity are
 * added here rather than twice, so a hand and the office are refused for the
 * same reasons and read the same sentence.
 *
 * ─── Why a blank rate or quantity is refused at all ────────────────────────
 *
 * The API does not refuse one. `tickets.routes.ts` guards its finite-number
 * check with `rate != null`, so a null rate is accepted and stored as NULL, and
 * the line reaches invoicing priced at nothing. Nobody is told. This is the
 * client being the only gate, which is stated plainly because it means the
 * check can be walked past by any other writer of that endpoint — the offline
 * queue replays a payload this screen already validated, so that path is
 * covered, but a future caller is not.
 *
 * ─── Why zero must be typed, and never filled in ───────────────────────────
 *
 * Zero is a real answer here: a no-charge line, a line discounted to nothing, a
 * standby hour nobody is billed for. A blank is the absence of an answer. The
 * two must not be collapsed, in either direction — refusing a typed 0 would
 * make the operator lie about a discount, and defaulting a blank to 0 would
 * quietly bill a customer nothing for work that was done. So `0` passes and
 * blank is refused, and nothing in this file writes a value into a row.
 */

export const RATE_QTY_REQUIRED_MESSAGE =
  'Enter a rate and quantity. Use 0 for a no-charge or discounted line.';

export const DESCRIPTION_REQUIRED_MESSAGE = 'Description is required';
export const TYPE_REQUIRED_MESSAGE = 'Type is required';

/**
 * The banner when the refusal's kinds cannot be named — see bannerForRowErrors.
 *
 * It was written for the server path and then never rendered there, because the
 * screens reached for the API's `error` string first and the API always sends
 * one. Now it is the fallback it was meant to be.
 */
export const ROW_ERRORS_GENERIC_MESSAGE =
  'Some line items have errors — see the marked lines below.';

/** The money fields, and the only ones this file marks separately from the row. */
export const MONEY_FIELDS = ['rate', 'quantity'] as const;

/** Only what is checked. Both editors' LineItem carry more and pass it whole. */
export interface ValidatableLineItem extends DiscountFields {
  description?: string | null;
  lineItemType?: string | null;
  rate?: number | null;
  quantity?: number | null;
}

export interface LineItemValidation {
  ok: boolean;
  /**
   * Per-row reasons, in the SAME shape `groupRowErrors` builds from the API's
   * 400 `details[]` — `{ message, field }` keyed by row index. That shared shape
   * is the whole mechanism by which a server refusal and a client one look
   * identical on screen: the render reads marks off `field` and cannot tell
   * which side produced the entry.
   */
  rowErrors: Record<number, RowErrorReason[]>;
  /** Type lives in its own map because both editors already render it apart. */
  typeErrors: Record<number, string>;
  /** The row to bring on screen — the first refused one, top to bottom. */
  firstInvalidIndex: number | null;
  /** The banner sentence, or null when nothing is wrong. */
  message: string | null;
}

/** A number the row can actually be priced from. `0` qualifies; null does not. */
function isPriced(v: number | null | undefined): boolean {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Check every row and report what is wrong with each, without touching state.
 *
 * Pure on purpose: the two editors set different pieces of state from the
 * result (one also closes a picker, one carries a review flag), so returning
 * the finding and letting each screen apply it keeps this testable on its own
 * and keeps the screens' own quirks out of here.
 */
export function validateLineItems(items: ValidatableLineItem[]): LineItemValidation {
  const rowErrors: Record<number, RowErrorReason[]> = {};
  const typeErrors: Record<number, string> = {};

  items.forEach((li, i) => {
    /*
     * A DISCOUNT LINE OWES DIFFERENT THINGS. Its rate, quantity and type are
     * the server's (lib/ticket-discount.ts), so asking the hand for them would
     * refuse every discount; what it owes instead is a percent or an amount,
     * and for an Issue a name. Blank is still refused and a typed 0 still
     * passes, by the same reasoning as the header above.
     */
    if (isDiscountLine(li)) {
      const discountReasons = validateDiscountLine(li as DiscountableLine, i, items as DiscountableLine[]);
      if (discountReasons.length > 0) rowErrors[i] = discountReasons;
      return;
    }
    const reasons: RowErrorReason[] = [];
    if (!li.description?.trim()) {
      reasons.push({ message: DESCRIPTION_REQUIRED_MESSAGE, field: 'description' });
    }
    /*
     * ONE SENTENCE, ONE OR TWO MARKS. A row missing both numbers gets a reason
     * per field so both boxes are marked, and the two carry the same words —
     * the reader is told what to do once. `dedupeMessages` collapses the
     * repeat at render; a second identical sentence under the same row reads
     * as a second problem.
     */
    for (const field of MONEY_FIELDS) {
      if (!isPriced(li[field])) reasons.push({ message: RATE_QTY_REQUIRED_MESSAGE, field });
    }
    if (reasons.length > 0) rowErrors[i] = reasons;
    if (!li.lineItemType) typeErrors[i] = TYPE_REQUIRED_MESSAGE;
  });

  const badRows = [
    ...Object.keys(rowErrors).map(Number),
    ...Object.keys(typeErrors).map(Number),
  ];
  const ok = badRows.length === 0;

  return {
    ok,
    rowErrors,
    typeErrors,
    firstInvalidIndex: ok ? null : Math.min(...badRows),
    message: ok ? null : bannerForRowErrors(rowErrors, typeErrors),
  };
}

/**
 * The banner above the list, which names the KIND of problem and nothing more.
 *
 * It is deliberately not a list of rows: the rows say which rows they are, and
 * a banner that repeats them is the thousand-character sentence the row marks
 * were built to replace.
 *
 * BOTH SOURCES OF A REFUSAL COME THROUGH HERE, and that is the point rather
 * than a tidiness win. The screens used to render the API's own `error` string
 * on the server path — "Invalid line items", a category label that names no
 * kind and, more to the point, does not tell the reader to look DOWN at the
 * rows this same refusal has just marked. So one defect had two sentences, and
 * the worse one was the half the reader could not act on. The API's string is
 * left alone; it is simply not what the operator reads.
 *
 * The type reason arrives spelled differently by the two sides — the local
 * check keeps it in its own `typeErrors` map, the API sends it as a row reason
 * with `field: 'lineItemType'` — so both are counted. A sentence that said "a
 * description" for a payload that also named the type would be quietly
 * incomplete, and nothing on screen would say so.
 */
export function bannerForRowErrors(
  rowErrors: Record<number, RowErrorReason[]>,
  typeErrors: Record<number, string> = {}
): string {
  const reasons = Object.values(rowErrors).flat();
  const missingDescription = reasons.some(r => r.field === 'description');
  const missingMoney = reasons.some(r => r.field === 'rate' || r.field === 'quantity');
  const missingType =
    Object.keys(typeErrors).length > 0 || reasons.some(r => r.field === 'lineItemType');
  const parts: string[] = [];
  if (missingDescription) parts.push('a description');
  if (missingType) parts.push('a type');
  if (missingMoney) parts.push('a rate or quantity');
  // A discount refusal (discount_percent / discount_amount / discount_kind) is
  // deliberately not named here: "percent must be under 100" or "already has a
  // Multi Rig discount" is not something MISSING, so it takes the generic
  // sentence below and the row carries the server's own words.
  /*
   * NOTHING RECOGNISED IS A REAL OUTCOME ON THE SERVER PATH, and it is why the
   * generic sentence below is not dead any more.
   *
   * The local check can only produce the fields it checks for, so it never
   * reaches here. A 400 can: a future `unit` reason, or a detail with no
   * `field` at all. Naming the parts anyway would render "Some line items are
   * missing  — see the marked lines below." — a sentence with a hole in it,
   * from a screen whose whole job this release is to say what is wrong. The
   * rows still carry the server's own words, so the banner only has to point
   * at them.
   *
   * This is `splitRowReasons`' complement rule in the other direction: there,
   * an unknown field must still get words on screen; here, it must still get a
   * banner that reads as a sentence.
   */
  if (parts.length === 0) return ROW_ERRORS_GENERIC_MESSAGE;
  return `Some line items are missing ${joinWords(parts)} — see the marked lines below.`;
}

function joinWords(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Does this row have something wrong with the named input? Drives its red edge.
 *
 * Reads the `field` the API has always sent and the screens used to drop, so a
 * server refusal about the rate marks the rate box exactly as a local one does.
 */
export function fieldHasError(
  reasons: RowErrorReason[] | undefined,
  field: string
): boolean {
  return !!reasons?.some(r => r.field === field);
}

/** The class for an input's edge, matching the type picker's existing treatment. */
export function fieldErrorClass(
  reasons: RowErrorReason[] | undefined,
  field: string
): string {
  return fieldHasError(reasons, field)
    ? 'border-red-500/60 text-red-400'
    : 'border-fieldiq-border';
}

/**
 * Split a row's reasons into the ones shown under the money row and the rest.
 *
 * THE COMPLEMENT IS THE DEFAULT, and that direction matters: a reason naming a
 * field neither bucket knows — a future `unit`, a server entry with no field at
 * all — falls into `other` and is still rendered. Bucketing by an allow-list on
 * both sides would let such a reason vanish while its row still wore a red
 * edge, which is a marked row with no words on it.
 */
export function splitRowReasons(reasons: RowErrorReason[] | undefined): {
  money: RowErrorReason[];
  other: RowErrorReason[];
} {
  const list = reasons ?? [];
  const isMoney = (r: RowErrorReason) => MONEY_FIELDS.includes(r.field as any);
  return {
    money: dedupeMessages(list.filter(isMoney)),
    other: dedupeMessages(list.filter(r => !isMoney(r))),
  };
}

/**
 * Collapse repeats of the same sentence, keeping the first.
 *
 * Only exact repeats: the server's "Rate must be a finite number" and
 * "Quantity must be a finite number" are different sentences and both survive.
 */
export function dedupeMessages(reasons: RowErrorReason[]): RowErrorReason[] {
  const seen = new Set<string>();
  return reasons.filter(r => (seen.has(r.message) ? false : (seen.add(r.message), true)));
}

/**
 * Drop this row's reasons about one input, keeping everything else wrong with it.
 *
 * Field-scoped rather than whole-row, because a row can be refused for two
 * things at once. Clearing the whole row when the rate is typed would erase a
 * live "Description is required" and let the row read as corrected when it
 * is not.
 */
export function clearFieldError(
  errors: Record<number, RowErrorReason[]>,
  index: number,
  field: string
): Record<number, RowErrorReason[]> {
  const current = errors[index];
  if (!current) return errors;
  const kept = current.filter(r => r.field !== field);
  if (kept.length === current.length) return errors;
  const next = { ...errors };
  if (kept.length > 0) next[index] = kept; else delete next[index];
  return next;
}

/**
 * Bring a refused row on screen.
 *
 * NOT ANIMATED, AND THAT IS LOAD-BEARING RATHER THAN A TASTE CALL. The app's
 * only scroll container is `<main>` inside an `h-screen` wrapper the layout
 * marks as not scrolling itself, and an animated `scrollIntoView` on that
 * container is inert in Chrome — measured on the real page 2026-09-07, the
 * animated call left scrollTop at 0 across 1.2s while the immediate one moved
 * it 1449px on the very next line. Nothing errors; the row simply never
 * arrives, which reads as the refusal not marking anything.
 *
 * A jsdom test CANNOT catch that. It stubs this method, so it can only pin
 * WHICH element is asked and never whether the page moved — the suites assert
 * the first refused row is the one handed over, and a browser is what proved
 * it travels. Immediate is also the better behaviour here: on a refusal the
 * reader wants the row, not 400ms of scenery.
 *
 * Optional-called because that jsdom stub is absent by default and a test
 * rendering the refusal would otherwise die inside the handler it is testing,
 * and because a null element — the row removed in the same tick — is a normal
 * outcome rather than an exception.
 */
export function scrollRowIntoView(el: HTMLElement | null | undefined): void {
  el?.scrollIntoView?.({ behavior: 'auto', block: 'center' });
}
